<script setup>
import { ref, watch } from "vue";
import { pluralize } from "common/utils/format/word";

const props = defineProps({
  oneInch: {
    type: Number,
    required: true
  },
  scale: {
    type: Number,
    required: true
  }
});

const componentRef = ref(null);
const style = ref(null);
const label = ref("0 inches");

watch(
  () => props.scale,
  scaledValue => {
    /* 30px padding on both sides */
    const wrapperWidth = Math.min(
      componentRef.value.clientWidth - 30 * 2,
      componentRef.value.clientHeight - 30 * 2
    );

    const calculateScale = baseScale => {
      const scaleWidth = Math.floor(scaledValue / baseScale);
      const scaleWidthDivided = Math.floor(scaleWidth / 2);
      const scaleWidthMultiplied = Math.floor(scaleWidth * 2);

      if (scaleWidth < props.oneInch && !(scaleWidthMultiplied > wrapperWidth)) {
        return calculateScale(baseScale / 2);
      }
      if (scaleWidth > wrapperWidth && !(scaleWidthDivided < props.oneInch)) {
        return calculateScale(baseScale * 2);
      }
      return {
        scaleFactor: baseScale,
        width: scaleWidth
      };
    };
    const { width, scaleFactor } = calculateScale(1);
    label.value = pluralize(1 / scaleFactor, "inch", "es");
    style.value = { width: `${width}px` };
  }
);
</script>

<template>
  <div ref="componentRef" class="image-review-measurement" data-testid="image-review-measurement">
    <div class="image-review-measurement__sizes-wrapper">
      <div class="image-review-measurement__size image-review-measurement__size--width">
        <div
          class="image-review-measurement__size-ruler"
          :style="style"
          data-testid="image-review-measurement-width"
        >
          {{ label }}
        </div>
      </div>
      <div class="image-review-measurement__size image-review-measurement__size--height">
        <div
          class="image-review-measurement__size-ruler"
          :style="style"
          data-testid="image-review-measurement-height"
        >
          {{ label }}
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.image-review-measurement {
  position: relative;
  height: 100%;
  padding: 30px 0 0 30px;
  overflow: hidden;

  &__sizes-wrapper {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  &__size {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;

    &--width {
      padding: 10px 0 0 30px;
      justify-content: flex-start;
    }

    &--height {
      justify-content: flex-end;
      transform: rotate(-90deg) translate(-30px, 10px);
    }
  }

  &__size-ruler {
    flex: none;
    color: $black;
    min-width: 100px;
    border: 2px solid currentColor;
    border-top: none;
    font-size: 13px;
    font-weight: 600;
    height: 17px;
    text-align: center;
    line-height: 8px;
  }
}
</style>
